<template>
    <transition name="nebula-modal-transition" mode="out-in">
        <NebulaModal
            v-if="showModal"
            class="description-sample-modal"
            @close-modal="toggleModal"
            size="m"
            :ariaLabelledby="modalId.title"
            :ariaDescribedby="modalId.subheading"
            :firstFocusedElement="modalId.title"
            :onCloseForcedFocusElementId="modalId.triggerElement"
        >
            <template v-slot:header>
                <h2 :id="modalId.title" :tabindex="-1">{{ title }}</h2>
                <p :id="modalId.subheading">{{ subheading }}</p>
            </template>
            <template v-slot:content>
                <div class="description-sample-modal__examples-container" role="list">
                    <div
                        v-for="(sampleText, idx) in sampleTexts"
                        :key="idx"
                        class="description-sample-modal__examples-content"
                        role="listitem"
                        :aria-setsize="sampleTexts.length"
                        :aria-posinset="idx + 1"
                        :aria-label="samples[idx]"
                    >
                        <p class="description-sample-modal__sample" v-html="sampleText" aria-hidden="true" />
                        <div class="description-sample-modal__button-container">
                            <NebulaButton
                                @click="copyToClipboard(idx)"
                                type="flat"
                                :icon-left="copyButtons[idx].icon"
                                size="s"
                                :text="copyButtons[idx].text"
                                :data-click-type="copyButtonTracking.dataClickType"
                                :data-click-name="copyButtonTracking.dataClickName"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </NebulaModal>
    </transition>
</template>

<script>
import { NebulaModal, NebulaButton } from '@discoveryedu/nebula-components';
import { useModalStore } from '@/stores/useModalStore';
import { MODAL_IDS } from '@/constants/modalIDs';

export default {
    setup() {
        const store = useModalStore();
        return { store, MODAL_IDS };
    },
    name: 'PresentExamplesModal',
    components: {
        NebulaButton,
        NebulaModal,
    },
    data() {
        const buttonTemplate = {
            text: this.$t('Copy Sample'),
            icon: 'copy-to-clipboard',
        };
        return {
            buttonTemplate,
            copyButtons: [],
        };
    },
    computed: {
        title() {
            return this.store.examplesModal.title;
        },
        subheading() {
            return this.store.examplesModal.subheading;
        },
        samples() {
            return this.store.examplesModal.samples || [];
        },
        copyButtonTracking() {
            return this.store.examplesModal.tracking;
        },
        showModal() {
            return this.store.examplesModal.show;
        },
        sampleTexts() {
            return this.samples.map((sample) => this.parsePlaceholder(sample));
        },
        modalId() {
            return this.MODAL_IDS.EXAMPLE_DESCRIPTION_MODAL;
        },
    },
    methods: {
        toggleModal() {
            this.store.toggleModal({
                category: 'examples',
                type: 'generic',
                show: !this.store.examplesModal.show,
            });
        },
        copyToClipboard(textIndex) {
            const sampleSelected = this.sampleTexts[textIndex];

            // Removes bold tags from sample text
            const blanksTrimmed = sampleSelected.replace(/(<b>)|(<\/b>)/g, '');

            // Add to clipboard
            navigator.clipboard.writeText(blanksTrimmed);

            this.copyButtons[textIndex].icon = 'circle-check--filled';
            this.copyButtons[textIndex].text = this.$t('Copied!');

            setTimeout(() => this.resetButtons(textIndex), 3000);
        },
        parsePlaceholder(text) {
            // Surround [brackets] with <b> tags, to bolden them in the document.
            const boldRegex = /(\[(?:\w|\s|,|-)+\])/g;
            const split = text.split(boldRegex);

            const boldenedText = split
                .map((t) => {
                    if (!t.match(boldRegex)) return t;
                    return `<b>${t}</b>`;
                })
                .join('');

            return boldenedText;
        },
        resetButtons(index) {
            this.copyButtons[index].icon = this.buttonTemplate.icon;
            this.copyButtons[index].text = this.buttonTemplate.text;
        },
    },
    watch: {
        samples: {
            handler(newSamples) {
                this.copyButtons = newSamples.map(() => ({ ...this.buttonTemplate }));
            },
            immediate: true,
        },
    },
};
</script>

<style lang="stylus">
.description-sample-modal {
    // nebula overrides per design
    .nebula-modal__actions-container {
        display: none;
    }

    &__sample {
        nebula-text-body-2();
        margin: 0;
    }

    &__button-container {
        margin-top: auto;
        display: flex;
        justify-content: center;
        padding-block-start: $nebula-space-half;
    }

    &__examples-container {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: stretch;
        flex-wrap: wrap;
        gap: $nebula-space-2x;
    }

    &__examples-content {
        background-color: $nebula-color-platform-interactive-100;
        border-radius: $nebula-border-radius-small;
        padding: $nebula-space-3x $nebula-space-2x;
        display: flex;
        flex-direction: column;

        flex: 1 0 300px;
    }
}
</style>
